/**
 * Dummy data for the Academy.
 *
 * IMPORTANT: This is placeholder/demo content only. No backend connection.
 * Later this whole structure will be served from the backend (Bunny CDN for
 * video hosting + Firebase for metadata/progress/access control).
 *
 * Hierarchy:
 *   Topic (Themengebiet)
 *     └─ Course (Kurs)            e.g. "Outreach-Basics"
 *          └─ Lesson (Lektion)    1 video + optional mini-exercise (quiz)
 *     └─ Value Vault              links, documents, resources
 */

// ── Tier system ───────────────────────────────────────────────
// Everyone starts as "Starter Account". The tier moves up every time a whole
// study track (topic) is fully completed. Badges (see below) are separate and
// are earned per completed course.
export const tiers = [
  {
    id: 'starter',
    name: 'Starter Account',
    requiredTracks: 0,
    blurb: 'Your starting point. Access to the foundations.',
  },
  {
    id: 'operator',
    name: 'Operator',
    requiredTracks: 1,
    blurb: 'First track done. The real work begins.',
  },
  {
    id: 'closer',
    name: 'Closer',
    requiredTracks: 2,
    blurb: 'You know the playbook. Now you run it.',
  },
  {
    id: 'elite',
    name: 'Elite',
    requiredTracks: 3,
    blurb: 'Top of the ladder. Full arsenal unlocked.',
  },
];

// ── Badge system ──────────────────────────────────────────────
// A badge is an achievement earned by completing a single course (NOT a tier).
// Each course references a badge via `badge: { id, name }`. The visual design
// for a badge id lives here in `badgeDesigns`, so the same id can be mapped to
// a design in the frontend/backend independently of where it is awarded.
export const badgeDesigns = {
  'cold-email-rookie': {
    color: '#6da7ed',
    description: 'Mastered the cold email fundamentals.',
  },
  'phone-warrior': {
    color: '#f0a868',
    description: 'Owns the first seconds on the phone.',
  },
  'deal-closer': {
    color: '#5fd0a8',
    description: 'Runs live calls and closes with confidence.',
  },
  'iron-mind': {
    color: '#9b8cff',
    description: 'Built the discipline to keep showing up.',
  },
};

// Resolve a badge id to its design, with a safe fallback so a missing design
// never breaks rendering.
export const getBadgeDesign = (badgeId) =>
  badgeDesigns[badgeId] || { color: '#6da7ed', description: '' };

// ── Quiz question type reference ──────────────────────────────
// 'multiple-choice' → question + options[] (+ correctIndex, demo only)
// 'text'            → question + free text answer
// 'email'          → question + subject field + body field
//
// A lesson is "done" once the video is watched (duration tracked) AND
// every quiz question has an answer. A course is "completed" once all of
// its lessons are done → unlocks the completion badge.

const dummyDescription =
  'In dieser Lektion lernst du die Kernmechanik kennen und wendest sie direkt in einer kurzen Mini-Übung an. Schau dir das Video bis zum Ende an und beantworte anschließend die Fragen, um die Lektion abzuschließen.';

const makeCheatsheets = () => [
  { id: 'cs-1', title: 'Cheatsheet · Quick Reference (PDF)', type: 'pdf', url: '#' },
  { id: 'cs-2', title: 'Swipe File · Templates', type: 'doc', url: '#' },
];

export const topics = [
  {
    id: 'outreach',
    title: 'Outreach',
    tagline: 'Cold to booked.',
    description:
      'Everything from first contact to a booked call. Frameworks, scripts and the mindset behind high-volume, high-quality outreach.',
    accent: '#6da7ed',
    image: '/img/demo__2.jpg',
    valueVault: [
      { id: 'vv-1', title: 'Outreach Tracker (Google Sheet)', type: 'link', url: '#' },
      { id: 'vv-2', title: 'Master Script Library', type: 'doc', url: '#' },
      { id: 'vv-3', title: 'Objection Handling Cheatsheet', type: 'pdf', url: '#' },
      { id: 'vv-4', title: 'Tool Stack Recommendations', type: 'link', url: '#' },
    ],
    courses: [
      {
        id: 'outreach-basics',
        title: 'Outreach-Basics',
        subtitle: 'The fundamentals of getting replies',
        badge: { id: 'cold-email-rookie', name: 'Cold Email Rookie' },
        description:
          'Build a repeatable outreach engine from scratch. Channels, messaging, follow-up cadence and the small details that move reply rates.',
        lessons: [
          {
            id: 'l1',
            title: 'E-Mail Copywriting Masterclass (WOCHE 1)',
            label: 'WOCHE 1',
            duration: '64:23',
            durationSeconds: 60, // demo watch length (sped up for the dummy)
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: {
              questions: [
                {
                  id: 'q1',
                  type: 'multiple-choice',
                  question: 'What is the single most important element of a cold email?',
                  options: [
                    'A long, detailed pitch',
                    'A relevant, personalized opening line',
                    'A flashy email signature',
                    'Multiple links to your website',
                  ],
                  correctIndex: 1,
                },
                {
                  id: 'q2',
                  type: 'text',
                  question: 'Write a one-sentence opening line for a prospect in the e-commerce space.',
                  placeholder: 'Type your opening line…',
                },
              ],
            },
            practiceLab: {
              mode: 'easy',
              title: 'Cold E-Mail aufbauen (geführt)',
              brief:
                'Kontakt: Geschäftsführer eines B2B Unternehmens, das gerade Vertriebler sucht, um ein eigenes Sales Team aufzubauen. Dein Ziel: ihm zeigen, dass du ihn direkt an neue Kunden bringst. Die Struktur der E-Mail ist vorgegeben und bleibt so. Ergänze nur die markierten Stellen und bleib in den Wortlimits.',
              charLimit: 600,
              email: {
                toName: 'Lukas Berger',
                to: 'l.berger@berger-industrietechnik.de',
                subjectPlaceholder: 'Überlege dir einen kurzen, neugierig machenden Betreff…',
                greeting: 'Sehr geehrter Herr Berger,',
                signoff: 'Beste Grüße\nMarvin Steinbach',
                note: 'Diese E-Mail soll höflich und professionell klingen. Ergänze nur die markierten Abschnitte und überschreite die Wortlimits nicht.',
              },
              context: {
                scenario:
                  'Du hast online ein Stelleninserat entdeckt: Berger Industrietechnik sucht gerade Vertriebler, um ein eigenes Sales Team aufzubauen. Dein Angebot: Du bringst sie direkt an neue Kunden, ohne dass sie monatelang ein Team aufbauen müssen.',
                facts: [
                  { label: 'Kontakt', value: 'Lukas Berger, Geschäftsführer' },
                  { label: 'Unternehmen', value: 'Berger Industrietechnik, B2B Zulieferer' },
                  { label: 'Signal', value: 'Stelleninserat: sucht 2 Vertriebler für den Aufbau eines Sales Teams' },
                  { label: 'Ziel', value: 'Mehr Anfragen und neue Kunden in der Pipeline' },
                ],
                notes: [
                  'Ein Sales Team aufzubauen dauert Monate: einstellen, einarbeiten, bis die ersten Termine kommen.',
                  'Vertriebsgehälter plus Provision sind ein hohes Fixrisiko, bevor der erste Kunde da ist.',
                  'Dein Angebot: gebuchte Termine mit passenden neuen Kunden, schneller und ohne Festanstellung.',
                ],
              },
              blocks: [
                {
                  id: 'icebreaker',
                  label: 'Eisbrecher',
                  hint: 'Persönlicher Aufhänger',
                  placeholder:
                    'Gehe freundlich auf das Stelleninserat ein, in dem sie Vertriebler für den Aufbau eines Sales Teams suchen. Zeig echtes Interesse statt Lob auf Vorrat.',
                  wordLimit: 30,
                },
                {
                  id: 'problem',
                  label: 'Problem',
                  hint: 'Der Schmerz, den er spürt',
                  placeholder:
                    'Ein Sales Team aufzubauen dauert Monate und ist teuer, neue Kunden braucht ihr aber jetzt. Sprich das behutsam an.',
                  wordLimit: 30,
                },
                {
                  id: 'solution',
                  label: 'Lösung',
                  hint: 'Deine Lösung in einem Satz',
                  placeholder:
                    'Erkläre einfach, dass du sie direkt an neue Kunden bringst, ohne langen Teamaufbau. Freundlicher Ton.',
                  wordLimit: 25,
                },
                {
                  id: 'proof',
                  label: 'Beweis',
                  hint: 'Warum du glaubwürdig bist',
                  placeholder:
                    'Nenne ein konkretes Ergebnis mit Zahl, zum Beispiel 23 gebuchte Termine in 6 Wochen für ein ähnliches Unternehmen.',
                  wordLimit: 25,
                },
                {
                  id: 'cta',
                  label: 'CTA',
                  hint: 'Eine einfache Aufforderung',
                  placeholder:
                    'Schlage höflich einen kurzen Call vor und frage nach einem passenden Zeitpunkt.',
                  wordLimit: 20,
                },
              ],
            },
          },
          {
            id: 'l2',
            title: 'E-Mail Copywriting Masterclass (HYBRID MODE)',
            label: 'HYBRID MODE',
            duration: '109:48',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: {
              id: 'st-1',
              title: 'Special Task · Build your first sequence',
              description:
                'Hardcoded exercise placeholder: a custom multi-step task can be wired in here for specific lessons.',
            },
            quiz: {
              questions: [
                {
                  id: 'q1',
                  type: 'email',
                  question: 'Draft a full cold email for a SaaS founder you want to book a call with.',
                  subjectPlaceholder: 'Subject line…',
                  bodyPlaceholder: 'Write your email…',
                },
              ],
            },
            practiceLab: {
              mode: 'hard',
              title: 'Cold E-Mail von Grund auf',
              brief:
                'Kontakt: Gründer einer Marketing-Agentur mit 20 Mitarbeitern. Ziel: einen 15-minütigen Call buchen. Vorgaben: mit einer relevanten Beobachtung einsteigen, unter dem Wortlimit bleiben, genau ein CTA. Schreibe Betreff und komplette E-Mail selbst.',
              charLimit: 700,
              wordLimit: 120,
              structure: [
                { label: 'Eisbrecher', hint: 'Persönlicher Aufhänger', weight: 0.15 },
                { label: 'Problem', hint: 'Der Schmerz, den er spürt', weight: 0.2 },
                { label: 'Lösung', hint: 'Deine Lösung in einem Satz', weight: 0.25 },
                { label: 'Beweis', hint: 'Warum du glaubwürdig bist (Zahl, Name, Ergebnis)', weight: 0.25 },
                { label: 'CTA', hint: 'Eine einfache, konkrete Aufforderung', weight: 0.15 },
              ],
              context: {
                scenario:
                  'Cold Outreach, keine bestehende Beziehung. Du zielst auf Agenturen, die kürzlich Stellen für Vertriebler ausgeschrieben haben.',
                facts: [
                  { label: 'Kontakt', value: 'Tom Reuter, Gründer' },
                  { label: 'Unternehmen', value: 'Reuter Media, Performance Marketing Agentur' },
                  { label: 'Größe', value: '20 Mitarbeitende' },
                  { label: 'Signal', value: 'Hat im letzten Monat 3 SDR-Stellen ausgeschrieben' },
                ],
                notes: [
                  'SDRs einzustellen = will Outbound skalieren, hat aber Mühe, die Pipeline zu füllen.',
                  'Der Gründer ist aktiv auf X und teilt klare Meinungen zu Cold E-Mails.',
                  'Dein Angebot: gebuchte Meetings auf Pay-per-Result-Basis.',
                ],
              },
            },
          },
          {
            id: 'l3',
            title: 'Pacing & Disqualification',
            label: 'PACING & DISQ',
            duration: '42:10',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: null, // lesson without a quiz – done once video is watched
            practiceLab: {
              mode: 'subject',
              title: 'Betreffzeilen-Sprint',
              brief:
                'Du hattest eine richtig gute Demo, danach Funkstille. Schreib 3 Betreffzeilen für eine kurze Follow-up-Mail, die Elena wieder zum Antworten bringt. Halte sie kurz und konkret und vermeide Spam-Wörter.',
              subjectCount: 3,
              charLimit: 60,
              angles: [
                {
                  name: 'Die kurze Frage',
                  hint: 'Eine Mini-Frage macht neugierig und ist leicht zu beantworten.',
                  example: 'Schlechtes Timing?',
                },
                {
                  name: 'Der Rückbezug',
                  hint: 'Greif ein Detail aus eurem Gespräch auf.',
                  example: 'Zu eurem Reporting-Wunsch',
                },
                {
                  name: 'Das Ergebnis',
                  hint: 'Sag in wenigen Worten, was sie davon hat.',
                  example: '15 Minuten, klare Zahlen',
                },
              ],
              context: {
                scenario:
                  'Vor 9 Tagen hattest du eine Demo mit Elena. Sie war begeistert und wollte es intern besprechen, seitdem hörst du nichts mehr. Eine freundliche Follow-up-Mail soll das Gespräch wieder öffnen.',
                facts: [
                  { label: 'Kontakt', value: 'Elena Vogt, VP Sales' },
                  { label: 'Unternehmen', value: 'Brightpath SaaS' },
                  { label: 'Letzter Kontakt', value: 'Live-Demo, vor 9 Tagen' },
                  { label: 'Status', value: 'Hat sich seitdem nicht gemeldet' },
                ],
                notes: [
                  'In der Demo hat ihr besonders das Reporting-Dashboard gefallen.',
                  'Sie wollte das Budget intern zum Quartalsende klären.',
                  'Ziel: locker erinnern und eine Antwort auslösen, ohne zu drängen.',
                ],
              },
            },
          },
          {
            id: 'l4',
            title: 'SalesGod Mode: Dynamische Templates',
            label: 'SALESGOD',
            duration: '37:52',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: null,
            practiceLab: {
              mode: 'salesgod',
              title: 'Dynamisches E-Mail-Template bauen',
              brief:
                'Hier schreibst du keine einzelne E-Mail, sondern ein Template, das SalesGod für jeden Kontakt automatisch personalisiert. Nutze {{greeting}} für die Anrede, doppelte Klammern {{ }} für feste Werte aus der Datenbank und einfache Klammern { } für KI-Prompts, die beim Versand pro Empfänger individuell generiert werden.',
              // Fixed variables available from the database. `special: true` marks
              // the reserved {{greeting}} snippet that handles the salutation.
              variables: [
                { token: 'greeting', label: 'Begrüßung', sample: 'Hi Sarah', special: true },
                { token: 'first_name', label: 'Vorname', sample: 'Sarah' },
                { token: 'business_name', label: 'Firmenname', sample: 'Brightpath SaaS' },
                { token: 'industry', label: 'Branche', sample: 'SaaS' },
                { token: 'sender_name', label: 'Dein Name', sample: 'Marvin' },
              ],
              // Example AI prompts (single braces) the user can drop in to learn the
              // pattern. These get generated per contact at send time.
              promptExamples: [
                'ein konkreter Bezug zu einem aktuellen Projekt der Firma',
                'ein Vorteil, der speziell zur Branche passt',
                'ein lockerer Übergang zum Terminvorschlag',
              ],
              starter: '{{greeting}}\n\n',
              context: {
                scenario:
                  'Du baust ein Template für eine Outbound-Kampagne an SaaS-Gründer. Statt 100 E-Mails einzeln zu tippen, baust du ein Template, das SalesGod pro Kontakt automatisch füllt.',
                facts: [
                  { label: 'Kampagne', value: 'Outbound an SaaS-Gründer' },
                  { label: 'Datenquelle', value: 'CRM mit Firmen- und Kontaktdaten' },
                  { label: 'Ziel', value: 'Ein Template, das für jeden Kontakt passt' },
                ],
                notes: [
                  'Doppelte Klammern {{ }} sind feste Werte aus der Datenbank, immer gleich ersetzt.',
                  'Einfache Klammern { } sind ein KI-Prompt, der pro Empfänger individuell generiert wird.',
                  '{{greeting}} ist ein Spezial-Snippet und kümmert sich um die passende Anrede.',
                ],
              },
            },
          },
        ],
      },
      {
        id: 'cold-calling',
        title: 'Cold Calling Intensive',
        subtitle: 'Own the phone',
        badge: { id: 'phone-warrior', name: 'Phone Warrior' },
        description:
          'Tonality, openers, and how to stay in control of the conversation. Turn a cold dial into a warm conversation.',
        lessons: [
          {
            id: 'l1',
            title: 'The First 8 Seconds',
            label: 'OPENERS',
            duration: '28:14',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: {
              questions: [
                {
                  id: 'q1',
                  type: 'multiple-choice',
                  question: 'What should your tonality convey in the first 8 seconds?',
                  options: ['Apologetic', 'Calm confidence', 'High energy hype', 'Robotic neutrality'],
                  correctIndex: 1,
                },
              ],
            },
          },
          {
            id: 'l2',
            title: 'Handling the Brush-Off',
            label: 'OBJECTIONS',
            duration: '35:52',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: null,
          },
        ],
      },
    ],
  },
  {
    id: 'sales',
    title: 'Sales & Closing',
    tagline: 'Run the call. Close the deal.',
    description:
      'The full sales conversation: discovery, framing, objection handling and closing. Includes real live-call breakdowns.',
    accent: '#9b8cff',
    image: '/img/demo__4.jpg',
    valueVault: [
      { id: 'vv-1', title: 'Discovery Question Bank', type: 'doc', url: '#' },
      { id: 'vv-2', title: 'Closing Frameworks (PDF)', type: 'pdf', url: '#' },
      { id: 'vv-3', title: 'CRM Pipeline Template', type: 'link', url: '#' },
    ],
    courses: [
      {
        id: 'live-calls',
        title: 'Live Calls',
        subtitle: 'Real calls, broken down',
        badge: { id: 'deal-closer', name: 'Deal Closer' },
        description:
          'Watch real sales and setup calls and learn exactly what works (and what doesn’t) frame by frame.',
        lessons: [
          {
            id: 'l1',
            title: 'Live Sales-Call: Daniela',
            label: 'DANIELA',
            duration: '50:29',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: {
              questions: [
                {
                  id: 'q1',
                  type: 'text',
                  question: 'Name one thing the closer did well during discovery.',
                  placeholder: 'Your observation…',
                },
              ],
            },
          },
          {
            id: 'l2',
            title: 'Live Setup-Call: Kilian & Ronja',
            label: 'KILIAN & RONJA',
            duration: '93:05',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: null,
          },
          {
            id: 'l3',
            title: 'Live Sales-Call: Caro & Elena',
            label: 'CARO & ELENA',
            duration: '71:33',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: null,
          },
        ],
      },
    ],
  },
  {
    id: 'mindset',
    title: 'Mindset & Discipline',
    tagline: 'Win the inner game.',
    description:
      'The mental operating system behind consistent performance. Routines, focus and how to stay in the game long term.',
    accent: '#5fd0a8',
    image: '/img/demo__1.jpg',
    valueVault: [
      { id: 'vv-1', title: 'Daily Routine Template', type: 'doc', url: '#' },
      { id: 'vv-2', title: 'Recommended Reading List', type: 'link', url: '#' },
    ],
    courses: [
      {
        id: 'foundations',
        title: 'Mental Foundations',
        subtitle: 'Build the base',
        badge: { id: 'iron-mind', name: 'Iron Mind' },
        description: 'The non-negotiables of staying consistent when motivation runs out.',
        lessons: [
          {
            id: 'l1',
            title: 'The Consistency Engine',
            label: 'ROUTINES',
            duration: '22:40',
            durationSeconds: 60,
            description: dummyDescription,
            cheatsheets: makeCheatsheets(),
            specialTask: null,
            quiz: null,
          },
        ],
      },
    ],
  },
];

// ── Placeholder media assignment ──────────────────────────────
// Cycle the demo thumbnails as lesson previews + course covers until real
// media (Bunny CDN) is wired up. Replace /public/img/demo__*.jpg to update.
const demoImages = ['/img/demo__1.jpg', '/img/demo__2.jpg', '/img/demo__3.jpg', '/img/demo__4.jpg'];

let demoCursor = 0;
topics.forEach((topic) => {
  topic.courses.forEach((course) => {
    course.lessons.forEach((lesson) => {
      if (!lesson.preview) lesson.preview = demoImages[demoCursor % demoImages.length];
      demoCursor += 1;
    });
    if (!course.cover) {
      course.cover = (course.lessons[0] && course.lessons[0].preview) || demoImages[0];
    }
  });
});

// ── Helper selectors ──────────────────────────────────────────
export const getTopic = (topicId) => topics.find((t) => t.id === topicId) || null;

// Count of lessons across all courses of a topic (for the topic preview info).
export const getTopicLessonCount = (topic) =>
  topic.courses.reduce((acc, c) => acc + c.lessons.length, 0);

export const getCourse = (topicId, courseId) => {
  const topic = getTopic(topicId);
  if (!topic) return null;
  return topic.courses.find((c) => c.id === courseId) || null;
};

export const getLesson = (topicId, courseId, lessonId) => {
  const course = getCourse(topicId, courseId);
  if (!course) return null;
  return course.lessons.find((l) => l.id === lessonId) || null;
};

// Total number of courses across all topics (used for tier progress demo).
export const totalCourseCount = topics.reduce((acc, t) => acc + t.courses.length, 0);

// Total number of study tracks (topics) — drives the tier system.
export const totalTrackCount = topics.length;
